'use client';

import { MoonIcon, SunIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useId } from 'react';

import { SidebarMenuButton } from '@/components/ui/sidebar';
import { Switch } from '@/components/ui/switch';

type ThemeToggleProps = {
    variant?: 'switch' | 'button';
};

const ThemeToggle = ({ variant = 'switch' }: ThemeToggleProps) => {
    const id = useId();
    const { resolvedTheme, setTheme } = useTheme();

    const isDarkMode = resolvedTheme === 'dark';
    const targetTheme = isDarkMode ? 'light' : 'dark';
    const buttonLabel = isDarkMode
        ? 'Switch to light mode'
        : 'Switch to dark mode';

    const toggleTheme = () => setTheme(targetTheme);

    if (variant === 'button') {
        return (
            <SidebarMenuButton
                type="button"
                size="lg"
                onClick={toggleTheme}
                aria-label={buttonLabel}
            >
                {isDarkMode ? (
                    <SunIcon className="size-4" />
                ) : (
                    <MoonIcon className="size-4" />
                )}
                <span className="grow text-sm leading-none font-normal text-nowrap overflow-ellipsis group-data-[collapsible=icon]:hidden">
                    {buttonLabel}
                </span>
            </SidebarMenuButton>
        );
    }

    return (
        <div
            className="group inline-flex items-center gap-2"
            data-state={
                resolvedTheme === undefined
                    ? 'loading'
                    : isDarkMode
                      ? 'checked'
                      : 'unchecked'
            }
        >
            <span
                id={`${id}-light`}
                className="group-data-[state=checked]:text-muted-foreground/70 cursor-pointer text-left text-sm font-medium"
                aria-controls={id}
                onClick={() => setTheme('light')}
            >
                <SunIcon className="size-4" aria-hidden="true" />
            </span>
            <Switch
                id={id}
                checked={isDarkMode}
                onCheckedChange={toggleTheme}
                aria-labelledby={`${id}-dark ${id}-light`}
                aria-label="Toggle between dark and light mode"
            />
            <span
                id={`${id}-dark`}
                className="group-data-[state=unchecked]:text-muted-foreground/70 cursor-pointer text-right text-sm font-medium"
                aria-controls={id}
                onClick={() => setTheme('dark')}
            >
                <MoonIcon className="size-4" aria-hidden="true" />
            </span>
        </div>
    );
};

export default ThemeToggle;
